import { buildCarrierColDef } from "./buildCarrierColDef";
import { formatCommissionDisplay } from "./formatting/formatCommissionDisplay";
import {
  COVERAGE_LABEL_FIELD,
  INCUMBENT_COL_KEY,
  REQUESTED_COL_KEY,
  type CarrierCommissionCell,
  type CompsGridContext,
  type CompsGridRow,
  type CompsMaterializeTarget,
  type RawCommissionAmount,
} from "./types";

import type {
  CompsTableCarrierRef,
  CompsTableCommissionAmount,
  CompsTableCommissionCell,
  CompsTableCommissionType,
  CompsTableCompsTableResponse,
  CompsTableIncumbentCommissionCell,
  CompsTableNoteCell,
  CompsTableRequestedCommissionCell,
  CompsTableSubsidyRowKind,
} from "@platform/api";
import type { ColDef } from "ag-grid-community";

/** BTX free-text payload field for each subsidy/credit row kind. */
const SUBSIDY_FREE_TEXT_FIELD: Record<CompsTableSubsidyRowKind, string> = {
  techCredit: "compsTechSubsidyText",
  implementationCredit: "compsImplementationCreditText",
  wellnessDollars: "compsWellnessDollarsText",
};

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

const bySequence = <T extends { sequence: number }>(a: T, b: T): number =>
  a.sequence - b.sequence;

/**
 * Orders carriers by the analyst's shared SBS carrier sequence (set from the
 * Quotes tab and applied across Responses/Comps/Financials, CD-1806) when
 * provided, falling back to backend `sequence` for any carrier the shared
 * order doesn't mention — e.g. one that hasn't quoted yet.
 */
const applyCarrierOrder = <T extends { id: string; sequence: number }>(
  carriers: readonly T[],
  carrierOrder: readonly string[] | undefined,
): T[] => {
  const sorted = carriers.toSorted(bySequence);
  if (!carrierOrder || carrierOrder.length === 0) {
    return sorted;
  }

  const rank = new Map<string, number>();
  carrierOrder.forEach((id, index) => rank.set(id, index));

  return sorted
    .map((carrier, index) => ({ carrier, index }))
    .toSorted((a, b) => {
      const rankA = rank.get(a.carrier.id) ?? Infinity;
      const rankB = rank.get(b.carrier.id) ?? Infinity;

      return rankA === rankB ? a.index - b.index : rankA - rankB;
    })
    .map(({ carrier }) => carrier);
};

const toRawAmount = (
  commissionType: string | null | undefined,
  amount: CompsTableCommissionAmount | null | undefined,
): RawCommissionAmount => {
  if (!amount) return null;
  switch (commissionType) {
    case "percentage":
    case "graded":
      return amount.percent ?? null;
    case "pepm":
      return amount.flat ?? null;
    case "heaped":
      return amount.heaped ?? null;
    default:
      return null;
  }
};

// ---------------------------------------------------------------------------
// Public helpers
// ---------------------------------------------------------------------------

/**
 * Formats a commission's type + amount into the single display string shown
 * in the Comps grid (e.g. "Heaped 20% year 1", "Flat 14%", "Carrier
 * Standard"). Normalizes the API's structured `CompsTableCommissionAmount`
 * shape to `RawCommissionAmount` before delegating to the shared formatter so
 * the Requested/Incumbent columns and carrier cells format identically.
 */
const formatRequestedCommission = (
  cell:
    | {
        commissionType?: CompsTableCommissionType | null;
        amount?: CompsTableCommissionAmount | null;
      }
    | undefined,
): string =>
  formatCommissionDisplay(
    cell && {
      commissionType: cell.commissionType,
      amount: toRawAmount(cell.commissionType, cell.amount),
    },
  );

// ---------------------------------------------------------------------------
// Lookup builders
// ---------------------------------------------------------------------------

/** Requested-column cells keyed by planGroupId. */
const buildRequestedLookup = (
  cells: CompsTableRequestedCommissionCell[],
): Map<string, CompsTableRequestedCommissionCell> =>
  new Map(cells.map((cell) => [cell.planGroupId, cell]));

/** Incumbent-column cells keyed by planGroupId. */
const buildIncumbentLookup = (
  cells: CompsTableIncumbentCommissionCell[],
): Map<string, CompsTableIncumbentCommissionCell> =>
  new Map(cells.map((cell) => [cell.planGroupId, cell]));

/**
 * Carrier commission cells, outer key = carrierId, inner key = planGroupId.
 */
const buildCarrierLookup = (
  cells: CompsTableCommissionCell[],
): Map<string, Map<string, CompsTableCommissionCell>> => {
  const outer = new Map<string, Map<string, CompsTableCommissionCell>>();
  cells.forEach((cell) => {
    const inner = outer.get(cell.carrierId) ?? new Map();
    inner.set(cell.planGroupId, cell);
    outer.set(cell.carrierId, inner);
  });

  return outer;
};

/** Notes keyed by column key ('requested', 'incumbent', or carrierId). */
const buildNotesLookup = (cells: CompsTableNoteCell[]): Map<string, string> => {
  const map = new Map<string, string>();
  cells.forEach((cell) => {
    const ref = cell.columnRef;
    if (ref.kind === "requested") {
      map.set(REQUESTED_COL_KEY, cell.text);
    } else if (ref.kind === "incumbent") {
      map.set(INCUMBENT_COL_KEY, cell.text);
    } else if (ref.kind === "carrier" && "carrierId" in ref) {
      map.set(ref.carrierId, cell.text);
    }
  });
  return map;
};

// ---------------------------------------------------------------------------
// Public builders
// ---------------------------------------------------------------------------

/**
 * Converts a `CompsTableCompsTableResponse` into AG Grid row data.
 *
 * Each requested coverage produces one commission row: coverage label +
 * combined commission display per column (see `formatCommissionDisplay`).
 *
 * The subsidy rows and the Notes row follow the coverage rows, in that order.
 * They are ordinary rows (not pinned) so the Coverages/Rows filter can hide
 * them; the Coverage-name column keeps them visually grouped at the bottom.
 *
 * Coverages are sorted by `sequence` ascending. The BFF excludes alternate-plan
 * coverages, so `coverages` only ever contains primaries. Returns an empty array
 * when there are no coverages, so the grid shows its empty state rather than a
 * lone Notes row.
 */
export const buildCompsRowData = (
  response: CompsTableCompsTableResponse,
): CompsGridRow[] => {
  const {
    coverages,
    carriers,
    requestedColumn,
    incumbentColumn,
    cells,
    notesRow,
    subsidyRows,
  } = response;

  const sortedCoverages = [...coverages].sort(bySequence);
  const sortedCarriers = [...carriers].sort(bySequence);

  const requestedLookup = buildRequestedLookup(requestedColumn.cells);
  const incumbentLookup = incumbentColumn
    ? buildIncumbentLookup(incumbentColumn.cells)
    : null;
  const carrierLookup = buildCarrierLookup(cells);
  const notesLookup = buildNotesLookup(notesRow.cells);

  // Coverage rows — one combined commission row per coverage
  const rows = sortedCoverages.map((coverage) => {
    const rowGroupId = coverage.planGroupId;
    const requestedCell = requestedLookup.get(coverage.planGroupId);
    const incumbentApiCell = incumbentLookup?.get(coverage.planGroupId);

    const carrierCells = Object.fromEntries(
      sortedCarriers.map((carrier) => {
        const apiCell = carrierLookup
          .get(carrier.id)
          ?.get(coverage.planGroupId);
        const cell: CarrierCommissionCell = {
          commissionType: apiCell?.commissionType ?? null,
          amount: toRawAmount(apiCell?.commissionType, apiCell?.amount),
          proposalStatus: apiCell?.proposalStatus,
          coverageStatus: apiCell?.coverageStatus,
        };
        return [carrier.id, cell] as const;
      }),
    );

    const incumbentCell: CarrierCommissionCell | undefined =
      incumbentColumn && incumbentApiCell
        ? {
            commissionType: incumbentApiCell.commissionType ?? null,
            amount: toRawAmount(
              incumbentApiCell.commissionType,
              incumbentApiCell.amount,
            ),
            proposalStatus: undefined,
            coverageStatus: undefined,
          }
        : undefined;

    const commissionRow: CompsGridRow = {
      rowId: `${rowGroupId}:commission`,
      rowGroupId,
      planGroupId: coverage.planGroupId,
      rowType: "commission",
      coverageLabel: coverage.label,
      [REQUESTED_COL_KEY]: formatRequestedCommission(requestedCell),
      ...(incumbentCell && { [INCUMBENT_COL_KEY]: incumbentCell }),
      ...carrierCells,
    };

    return commissionRow;
  });

  // Subsidy rows — after coverages, sorted by sequence, before notes. Ids are
  // keyed by kind (not position) so they stay stable across refetches, keeping
  // the hidden-row set aligned.
  const subsidyRowsData: CompsGridRow[] = [...(subsidyRows ?? [])]
    .sort(bySequence)
    .map((row) => {
      const gridRow: CompsGridRow = {
        rowId: `subsidy:${row.kind}`,
        rowGroupId: `subsidy:${row.kind}`,
        rowType: "subsidy",
        subsidyKind: row.kind,
        coverageLabel: row.label,
        [REQUESTED_COL_KEY]: "",
        ...(incumbentColumn && { [INCUMBENT_COL_KEY]: "" }),
      };

      row.cells.forEach((cell) => {
        const ref = cell.columnRef;
        const value =
          cell.text ?? (cell.amount != null ? String(cell.amount) : "");

        if (ref.kind === "requested") {
          gridRow[REQUESTED_COL_KEY] = value;
        } else if (ref.kind === "incumbent") {
          gridRow[INCUMBENT_COL_KEY] = value;
        } else if (ref.kind === "carrier" && "carrierId" in ref) {
          gridRow[ref.carrierId] = value;
        }
      });

      return gridRow;
    });

  // Notes row — always last in pinned bottom
  const notesRowData: CompsGridRow = {
    rowId: "notes",
    rowGroupId: "notes",
    rowType: "notes",
    coverageLabel: notesRow.label,
    [REQUESTED_COL_KEY]: notesLookup.get(REQUESTED_COL_KEY) ?? "",
    ...(incumbentColumn && {
      [INCUMBENT_COL_KEY]: notesLookup.get(INCUMBENT_COL_KEY) ?? "",
    }),
  };

  sortedCarriers.forEach((carrier) => {
    notesRowData[carrier.id] = notesLookup.get(carrier.id) ?? "";
  });

  if (rows.length === 0) return [];

  return [...rows, ...subsidyRowsData, notesRowData];
};

/**
 * Builds AG Grid column definitions from the API response.
 *
 * Column order:
 *   1. Coverage name — pinned left, sticky, row drag handle
 *   2. Inforce Commission — pinned left, only when present
 *   3. Requested Commission — pinned left
 *   4. Carrier columns — ordered by the shared SBS carrier sequence when set
 *      (CD-1806), otherwise by `sequence`; scrollable
 */
export const buildCompsColDefs = (
  response: CompsTableCompsTableResponse,
  carrierOrder?: readonly string[],
): ColDef<CompsGridRow>[] => {
  const { carriers, requestedColumn, incumbentColumn } = response;
  const sortedCarriers = applyCarrierOrder(carriers, carrierOrder);

  const coverageCol: ColDef<CompsGridRow> = {
    field: COVERAGE_LABEL_FIELD,
    headerName: "Coverage",
    pinned: "left",
    lockPinned: true,
    rowDrag: (params) => params.data?.rowType === "commission",
    minWidth: 200,
    suppressMovable: true,
    cellStyle: {
      fontWeight: 500,
      whiteSpace: "normal",
      wordBreak: "break-word",
      display: "flex",
      alignItems: "center",
      justifyContent: "flex-start",
      textAlign: "left",
    },
  };

  // Commission cells are pre-formatted strings for this column (see
  // buildCompsRowData), so buildCarrierColDef's commission editing branches
  // never engage — only the notes/subsidy rows become editable, letting
  // analysts enter free text for Requested the same way they do for
  // carriers and Incumbent.
  const requestedCol: ColDef<CompsGridRow> = {
    ...buildCarrierColDef({
      id: REQUESTED_COL_KEY,
      name: requestedColumn.label,
      sequence: 0,
    }),
    pinned: "left",
    lockPinned: true,
    minWidth: 160,
    flex: undefined,
    suppressMovable: true,
  };

  const incumbentCol: ColDef<CompsGridRow> | null = incumbentColumn
    ? {
        ...buildCarrierColDef({
          id: INCUMBENT_COL_KEY,
          name: incumbentColumn.label,
          sequence: 0,
        }),
        pinned: "left",
        lockPinned: true,
        minWidth: 160,
        flex: undefined,
        suppressMovable: true,
      }
    : null;

  const carrierCols = sortedCarriers.map((carrier: CompsTableCarrierRef) =>
    buildCarrierColDef(carrier),
  );

  return [
    coverageCol,
    ...(incumbentCol ? [incumbentCol] : []),
    requestedCol,
    ...carrierCols,
  ];
};

/**
 * Builds the AG Grid context object for the Comps grid.
 *
 * Provides the mutation-layer lookups that `onCellValueChanged` needs:
 * carrier proposal IDs, cell-level plan IDs, and free-text field mappings.
 *
 * The BFF excludes alternate-plan coverages, so `response.coverages` only
 * ever contains primaries.
 */
export const buildCompsContext = (
  response: CompsTableCompsTableResponse,
): CompsGridContext => {
  const proposalIdByCarrierId = new Map<string, string>(
    response.carriers
      .filter((c) => c.proposalId != null)
      .map((c) => [c.id, c.proposalId as string]),
  );

  const proposalPlanIdByCellKey = new Map<string, string>();
  const proposalIdByCellKey = new Map<string, string>();
  const materializeTargetByRowGroupId = new Map<
    string,
    CompsMaterializeTarget
  >();

  const carrierLookup = buildCarrierLookup(response.cells);
  const incumbentLookup = response.incumbentColumn
    ? buildIncumbentLookup(response.incumbentColumn.cells)
    : null;
  const carrierByName = incumbentLookup
    ? new Map(response.carriers.map((c) => [c.name, c]))
    : null;

  [...response.coverages].sort(bySequence).forEach((coverage) => {
    const rowGroupId = coverage.planGroupId;

    // Materialize key for authoring an analyst proposal plan when an analyst
    // edits an unquoted carrier cell in this coverage row (ADR-024).
    if (
      coverage.rfpBenefitPlanGroupId != null &&
      coverage.planPosition != null
    ) {
      materializeTargetByRowGroupId.set(rowGroupId, {
        rfpBenefitPlanGroupId: coverage.rfpBenefitPlanGroupId,
        planPosition: coverage.planPosition,
      });
    }

    response.carriers.forEach((carrier) => {
      const carrierCell = carrierLookup
        .get(carrier.id)
        ?.get(coverage.planGroupId);
      if (carrierCell?.proposalPlanId) {
        proposalPlanIdByCellKey.set(
          `${carrier.id}:${rowGroupId}`,
          carrierCell.proposalPlanId,
        );
      }
      if (carrierCell?.proposalId) {
        proposalIdByCellKey.set(
          `${carrier.id}:${rowGroupId}`,
          carrierCell.proposalId,
        );
      }
    });

    if (incumbentLookup && carrierByName) {
      const incumbentCell = incumbentLookup.get(coverage.planGroupId);
      if (!incumbentCell?.currentCarrierName) return;

      const carrier = carrierByName.get(incumbentCell.currentCarrierName);
      if (!carrier) return;

      const proposalId = proposalIdByCarrierId.get(carrier.id);
      const proposalPlanId = carrierLookup
        .get(carrier.id)
        ?.get(coverage.planGroupId)?.proposalPlanId;

      if (proposalId) {
        proposalIdByCellKey.set(
          `${INCUMBENT_COL_KEY}:${rowGroupId}`,
          proposalId,
        );
      }
      if (proposalPlanId) {
        proposalPlanIdByCellKey.set(
          `${INCUMBENT_COL_KEY}:${rowGroupId}`,
          proposalPlanId,
        );
      }
    }
  });

  // Map subsidy rows and the notes row to their BTX free-text payload field
  // names, keyed by the same kind-based rowId that buildCompsRowData assigns.
  const freeTextFieldByRowId = new Map<string, string>(
    (response.subsidyRows ?? []).map((row) => [
      `subsidy:${row.kind}`,
      SUBSIDY_FREE_TEXT_FIELD[row.kind],
    ]),
  );

  freeTextFieldByRowId.set("notes", "compsNotesText");

  return {
    proposalIdByCarrierId,
    proposalIdByCellKey,
    proposalPlanIdByCellKey,
    materializeTargetByRowGroupId,
    freeTextFieldByRowId,
  };
};
